/**
 * Global authentication redirect manager.
 * This allows non-React code (like axios interceptors) to trigger authentication redirects
 * using the PKCE library's logIn function from React components.
 */

type AuthRedirectCallback = () => void;

let authRedirectCallback: AuthRedirectCallback | null = null;

/**
 * Sets the authentication redirect callback.
 * This should be called from a React component that has access to AuthContext.
 * @param callback The function to call when redirecting to login (typically AuthContext.logIn)
 */
export const setAuthRedirectCallback = (callback: AuthRedirectCallback): void => {
  authRedirectCallback = callback;
};

/**
 * Clears the authentication redirect callback.
 * Useful for cleanup on unmount.
 */
export const clearAuthRedirectCallback = (): void => {
  authRedirectCallback = null;
};

/**
 * Triggers a redirect to the login page.
 * This can be called from anywhere in the app (components, services, error handlers).
 * Falls back to window.location redirect if no callback is set.
 */
export const redirectToLogin = (): void => {
  if (authRedirectCallback) {
    authRedirectCallback();
  } else {
    // Fallback for cases where the callback hasn't been set yet
    // This shouldn't normally happen but provides a safety net
    window.location.href = '/dashboard';
  }
};

